/**
 * Orders v2 service.
 *
 * @author Ryan Sweeney <rsweeney@buildcentrix.com>
 * @copyright (c) 2024 Build Centrix
 */
import { Inject, Injectable } from '@angular/core';
import { isEmpty } from 'lodash';
import { BehaviorSubject } from 'rxjs';
import { OrderFilterModel } from '../../models/filters/orders/order-filter.model';
import { WdOrderShippingOptionsService } from './options/shipping/wd-order-shipping-options.service';
import { TranslateService } from '@ngx-translate/core';
import { WdLocalSharedFunctionsService } from '../shared/wd-local-shared-functions.service';
import { WdLocalEnvironmentService } from '../environment/wd-local-environment.service';
import { WdWorkOrderReportService } from './reports/wd-work-order-report.service';
import { SelectionModel } from '@angular/cdk/collections';
import { ActivatedRoute } from '@angular/router';
import {
  IFulfillmentGroupModel,
  ILoadingZoneModel,
  IOrderDateTypeFilter,
  IOrderExportModel,
  IOrderFiltersModel,
  IOrderModel,
  IOrderOptionsModel,
  IOrderShippingNotifications,
  IOrderTabs,
  IOrderViewSettingsModel,
  IShippingTypeModel,
  ITableColumnOptions,
} from '../../interfaces/orders/order-interfaces';
import { WdOrderStatusService } from './statuses/wd-order-status.service';
import {
  IBcxDateTimeHelper,
  IBcxErrorService,
  IBcxOauth2HttpService,
  IBcxSharedFunctionsService,
  IBcxStorageService,
} from '../../interfaces/generic/generic-interfaces';
import angular from 'angular';
import { WdUserService } from '../users/wd-users.service';
import { IVehicleAssignmentModel } from '../../interfaces/vehicles/vehicle-interfaces';

@Injectable({
  providedIn: 'root',
})
export class WdOrdersV2Service {
  // Local Storage Key
  localStorageKey = 'orderAdminV2New';

  // Fulfillment Group Column Prefix
  fulfillmentColumnPrefix = 'fgCol-';

  // Tabbed Order Content
  tabbedOrderContent: Array<IOrderTabs> = [
    {
      key: 'details',
      label: 'Details',
      iconKey: 'menu',
      isActive: true,
      isVisible: true,
      canToggle: true,
    },
    {
      key: 'production',
      label: 'Production',
      iconKey: 'manufacturingAdmin',
      isActive: false,
      isVisible: true,
      canToggle: true,
    },
    {
      key: 'fulfillment',
      label: 'Fulfillment',
      iconKey: 'ok',
      isActive: false,
      isVisible: true,
      canToggle: true,
    },
    {
      key: 'shipping',
      label: 'Shipping',
      iconKey: 'shipped',
      isActive: false,
      isVisible: true,
      canToggle: true,
    },
  ];
  customTabCount = 0;

  // Order View Settings
  // Note: We save the last know total of each group/status/etc. in order limit what parameters we send in the
  // initial data load. Without it, we would have to send all parameters because we don't know which are selected.
  orderViewSettings: IOrderViewSettingsModel = {
    page: 1,
    pageSize: 25,
    groups: {
      fulfillment: {
        showEmpty: true,
      },
    },
    selected: {
      columns: { lookahead: {}, work: {} },
      groups: {
        fulfillment: {
          total: null, // (Last Known) Total Fulfillment Groups [Number]
          indexes: null, // Selected Fulfillment Groups [Comma separated indexes]
        },
        user: {
          total: null, // (Last Known) Total User Groups [Number]
          ids: null, // Selected User Groups [Comma separated ids]
        },
      },
      statuses: {
        order: {
          total: null, // (Last Known) Total Order Statuses [Number]
          ids: null, // Selected Order Statuses [Comma separated ids]
        },
        production: {
          total: null, // (Last Known) Total Production Statuses [Number]
          indexes: null, // Selected Production Statuses [Comma separated indexes]
        },
        shipping: {
          total: null, // (Last Known) Total Shipping Statuses [Number]
          indexes: null, // Selected Shipping Statuses [Comma separated indexes]
        },
      },
      types: {
        seat: {
          total: null, // (Last Known) Total Seat Types [Number]
          values: null, // Selected Seat Types [Comma separated values]
        },
        shipping: {
          total: null, // (Last Known) Total Shipping Types [Number]
          values: null, // Selected Shipping Types [Comma separated values]
        },
      },
    },
    sortKey: '_id',
    sortDir: 'desc',
    selectedTab: this.tabbedOrderContent[0],
  };

  // Variables
  allOrders = new BehaviorSubject<Array<object>>([]);
  allOrdersSelected = new SelectionModel(true, [] as any);

  // Booleans
  isLoadingOrders = new BehaviorSubject(true); // Loading Orders List
  isReloadingOrders = new BehaviorSubject(false); // Reloading Orders List
  isUpdatingOrder = new BehaviorSubject(false); // Updating Single Order

  // All and Visible List Order Columns
  allColumnOptions: Array<ITableColumnOptions> = [];
  orderListColumns: Array<string> = [];
  orderListColumnsOptions: Array<ITableColumnOptions> = [];

  // Date Type Filter Options
  dateTypeFilterOptions: Array<IOrderDateTypeFilter> = [
    {
      label: 'Production Dates',
      value: 'production',
      type: 'inProduction',
    },
    {
      label: 'Shipping Dates',
      value: 'delivery',
      type: 'shipped',
    },
  ];

  // Orders Filters
  orderFilters = new BehaviorSubject<IOrderFiltersModel>({
    dateStart: undefined,
    dateEnd: undefined,
    dateType: undefined,
    deliveryStatuses: undefined,
    fulfillmentGroups: undefined,
    jobFilter: undefined,
    orderShippingTypes: undefined,
    orderNumberFilter: undefined,
    orderStatuses: undefined,
    orderedForFilter: undefined,
    organizations: undefined,
    organizationFilter: undefined,
    productionStatuses: undefined,
    seatTypes: undefined,
    userGroups: undefined,
    users: undefined,
  });

  // Orders Options
  orderOptions = new BehaviorSubject<IOrderOptionsModel>({
    orderShippingTypes: undefined,
  });

  // Current live filter values (for reference)
  currentLiveFilters: IOrderFiltersModel = {
    dateStart: undefined,
    dateEnd: undefined,
    dateType: undefined,
    jobFilter: undefined,
    orderNumberFilter: undefined,
    orderedForFilter: undefined,
    organizationFilter: undefined,
  };

  // Order Fulfillment Group Variables
  orderFulfillmentCountMax = 0;
  defaultTabIcon = 'user';
  defaultCustomTabIcon = 'user';

  // Work Order Notifications Variables
  orderShippingNotifications: Array<IOrderShippingNotifications> = [];

  /**
   * Constructor Initialization
   * @since 4.71.0
   * @memberof WdOrdersV2Service
   */
  constructor(
    private route: ActivatedRoute,
    private translate: TranslateService,
    private wdEnvironmentService: WdLocalEnvironmentService,
    private wdTranslate: TranslateService,
    public wdLocalSharedFunctions: WdLocalSharedFunctionsService,
    public wdOrderReportService: WdWorkOrderReportService,
    public wdOrderShippingOptionsService: WdOrderShippingOptionsService,
    public wdUserService: WdUserService,
    @Inject('wdDateTimeHelper') public wdDateTimeHelper: IBcxDateTimeHelper,
    @Inject('wdErrorService') public wdErrorService: IBcxErrorService,
    @Inject('wdOauth2HttpService')
    public wdOauth2HttpService: IBcxOauth2HttpService,
    @Inject('wdSharedFunctionsService')
    public wdSharedFunctionsService: IBcxSharedFunctionsService,
    @Inject('wdStorageService') public wdStorageService: IBcxStorageService,
  ) {}

  /************************************************* async functions **************************************************/

  /**
   * Set custom tabs based on user ID
   * @since 4.77.0
   * @memberof WdOrdersV2Service
   */
  async initializeUserCustomTabs() {
    // Get current logged-in user based on ID
    const currentUser = this.wdUserService.currentUser.value;
    // Get custom tabs for user (if applicable)
    const customTabs = this.wdSharedFunctionsService.getDescendantProperty(
      currentUser,
      'settings.orders.list.tabs.custom',
    );
    // Get default tabs for user (if applicable)
    const defaultTabs = this.wdSharedFunctionsService.getDescendantProperty(
      currentUser,
      'settings.orders.list.tabs.default',
    );
    if (defaultTabs) {
      // Create a mapping of keys to isVisible values from defaultTabs
      const visibilityMap = defaultTabs.reduce((map, tab) => {
        map[tab.key] = tab.isVisible;
        return map;
      }, {});
      // Update isVisible in tabbedOrderContent based on visibilityMap
      this.tabbedOrderContent = this.tabbedOrderContent.map(
        (tab: IOrderTabs) => ({
          ...tab,
          isVisible:
            visibilityMap[tab.key] !== undefined
              ? visibilityMap[tab.key]
              : tab.isVisible,
        }),
      );
    }
    if (customTabs) {
      // Flag saved user tabs as custom tabs
      customTabs.forEach((tab: IOrderTabs) => {
        tab.isCustom = true;
        tab.isVisible = true;
      });
      const coreTabs = this.tabbedOrderContent.filter(
        (tab: IOrderTabs) => !tab.isCustom,
      );
      this.customTabCount = customTabs.length;
      this.tabbedOrderContent = coreTabs.concat(customTabs);
    }
  }

  /**
   * Get Orders from the API
   *
   * @since 4.71.0
   * @memberof WdOrdersV2Service
   */
  async fetchOrders(
    showLoader = true,
    initFilters = false,
  ): Promise<Array<object>> {
    // Orders List
    let orders: Array<any> = [];
    // Toggle Loader
    showLoader
      ? this.isLoadingOrders.next(true)
      : this.isReloadingOrders.next(true);
    try {
      // Set Filters
      const serializedFilters = this.getSerializedOrderFilters(
        this.orderFilters,
      );
      const params = this.wdLocalSharedFunctions.buildFilters(
        serializedFilters,
      );
      // Set Path
      const path = initFilters ? 'workOrders/loadData' : 'workOrders/orders';

      // Get Orders Data
      const response = await this.wdOauth2HttpService.get(
        `${this.wdLocalSharedFunctions.dataExportApiUrl}${path}${
          params ? '?' + params : ''
        }`,
      );
      // Set Filters
      if (initFilters) {
        this.setFiltersData(response.data);
        this.setOptionsData(response.data);
        // Set custom tabs
        await this.initializeUserCustomTabs();
      }
      // Set notifications data from response
      this.setNotificationsData(response.data);
      // Set Orders
      orders = this.parseOrders(response.data);
      // Return Filtered Orders
      return orders;
    } catch (error) {
      // Error Message
      this.wdErrorService.userError(
        this.wdTranslate.instant('labels.error'),
        this.wdTranslate.instant('outputs.failedToLoad', {
          type: this.wdTranslate.instant('order.title.singular'),
        }),
      );
      return this.allOrders.value;
    } finally {
      // Toggle Loader
      this.isLoadingOrders.next(false);
      this.isReloadingOrders.next(false);
      // Set Orders
      this.allOrders.next(orders);
    }
  }

  /**
   * PUT Updates fulfillment group status for an order
   *
   * @since 4.71.0
   * @memberof WdOrdersV2Service
   */
  async updateFulfillmentGroupStatus(
    index: string,
    groupIndex: string,
    statusIndex: string,
  ): Promise<void> {
    // Toggle Loader
    this.isUpdatingOrder.next(true);
    try {
      await this.wdOauth2HttpService.put(
        `${this.wdLocalSharedFunctions.orderApiUrl}orders/${index}?idType=indexId`,
        {
          fulfillmentGroupId: groupIndex,
          fulfillmentStatusId: statusIndex,
        },
      );
      // Success Message
      this.wdErrorService.userSuccess(
        this.wdTranslate.instant('labels.success'),
        this.wdTranslate.instant('outputs.successfullyUpdated', {
          type: this.wdTranslate.instant(
            'order.fulfillment.groups.status.title',
          ),
        }),
      );
    } catch (error) {
      // Error Message
      this.wdErrorService.userError(
        this.wdTranslate.instant('labels.error'),
        this.wdTranslate.instant('outputs.errorUpdating', {
          type: this.wdTranslate.instant(
            'order.fulfillment.groups.status.title',
          ),
        }),
      );
    } finally {
      // Toggle Loader
      this.isUpdatingOrder.next(false);
    }
  }

  /**
   * PUT Updates Date for an Order
   *
   * @since 4.71.0
   * @memberof WdOrdersV2Service
   */
  async updateOrderDate(
    index: string,
    type: string,
    target: string,
    date: string,
  ): Promise<void> {
    // Toggle Loader
    this.isUpdatingOrder.next(true);
    try {
      const updateDate = this.wdDateTimeHelper.getDateAsString(
        date,
        'YYYY-MM-DD',
      );
      const updateData: any = {};
      if (type === 'production') {
        if (target === 'start') {
          updateData.productionDate = updateDate;
        }
        if (target === 'end') {
          updateData.productionEndDate = updateDate;
        }
      } else if (type === 'delivery') {
        updateData.shippingDate = updateDate;
      }
      await this.wdOauth2HttpService.put(
        `${this.wdLocalSharedFunctions.orderApiUrl}orders/${index}?idType=indexId`,
        updateData,
      );
      // Success Message
      this.wdErrorService.userSuccess(
        this.wdTranslate.instant('labels.success'),
        this.wdTranslate.instant('outputs.successfullyUpdated', {
          type: this.wdTranslate.instant('order.dates.' + type + '.title'),
        }),
      );
    } catch (error) {
      // Error Message
      this.wdErrorService.userError(
        this.wdTranslate.instant('labels.error'),
        this.wdTranslate.instant('outputs.errorUpdating', {
          type: this.wdTranslate.instant('order.dates.' + type + '.title'),
        }),
      );
    } finally {
      // Toggle Loader
      this.isUpdatingOrder.next(false);
    }
  }

  /**
   * PUT Updates the job override value for an order
   *
   * @since 4.71.0
   * @memberof WdOrdersV2Service
   */
  async updateOrderJobOverride(index: string, updateData: any): Promise<void> {
    // Toggle Loader
    this.isUpdatingOrder.next(true);
    try {
      const { jobId, jobName, jobNumber } = updateData;
      const putData = {
        jobId,
        jobOverrideName: jobName,
        jobOverrideNumber: jobNumber,
      };
      await this.wdOauth2HttpService.put(
        `${this.wdLocalSharedFunctions.orderApiUrl}ecommerce/jobOverrides/${index}`,
        putData,
      );
      // Success Message
      this.wdErrorService.userSuccess(
        this.wdTranslate.instant('labels.success'),
        this.wdTranslate.instant('outputs.successfullyUpdated', {
          type: this.wdTranslate.instant('order.title.singular'),
        }),
      );
    } catch (error) {
      // Error Message
      this.wdErrorService.userError(
        this.wdTranslate.instant('labels.error'),
        this.wdTranslate.instant('outputs.errorUpdating', {
          type: this.wdTranslate.instant('order.title.singular'),
        }),
      );
    } finally {
      // Toggle Loader
      this.isUpdatingOrder.next(false);
    }
  }

  /**
   * PUT Updates overall status of several orders
   *
   * @since 4.71.0
   * @memberof WdOrdersV2Service
   */
  async updateOrderStatuses(
    indexes: Array<string>,
    status: string,
  ): Promise<void> {
    // Toggle Loader
    this.isUpdatingOrder.next(true);
    try {
      await this.wdOauth2HttpService.put(
        `${this.wdLocalSharedFunctions.orderApiUrl}orders`,
        {
          action: 'updateStatus',
          orderIndexes: indexes,
          newOrderStatusId: status,
        },
      );
      // Success Message
      this.wdErrorService.userSuccess(
        this.wdTranslate.instant('labels.success'),
        this.wdTranslate.instant('outputs.successfullyUpdated', {
          type: this.wdTranslate.instant('order.statuses.overall.title'),
        }),
      );
    } catch (error) {
      // Error Message
      this.wdErrorService.userError(
        this.wdTranslate.instant('labels.error'),
        this.wdTranslate.instant('outputs.errorUpdating', {
          type: this.wdTranslate.instant('order.statuses.overall.title'),
        }),
      );
    } finally {
      // Toggle Loader
      this.isUpdatingOrder.next(false);
    }
  }

  /**
   * PUT Updates overall status of an order
   *
   * @since 4.71.0
   * @memberof WdOrdersV2Service
   */
  async updateOrderStatus(index: string, status: string): Promise<void> {
    // Toggle Loader
    this.isUpdatingOrder.next(true);
    try {
      await this.wdOauth2HttpService.put(
        `${this.wdLocalSharedFunctions.orderApiUrl}orders/${index}?idType=indexId`,
        {
          statusId: status,
        },
      );
      // Success Message
      this.wdErrorService.userSuccess(
        this.wdTranslate.instant('labels.success'),
        this.wdTranslate.instant('outputs.successfullyUpdated', {
          type: this.wdTranslate.instant('order.status.overall.title'),
        }),
      );
    } catch (error) {
      // Error Message
      this.wdErrorService.userError(
        this.wdTranslate.instant('labels.error'),
        this.wdTranslate.instant('outputs.errorUpdating', {
          type: this.wdTranslate.instant('order.status.overall.title'),
        }),
      );
    } finally {
      // Toggle Loader
      this.isUpdatingOrder.next(false);
    }
  }

  /**
   * PUT Updates shipping option of an order
   *
   * @since 4.71.0
   * @memberof WdOrdersV2Service
   */
  async updateShippingOption(index: string, option: number): Promise<void> {
    // Toggle Loader
    this.isUpdatingOrder.next(true);
    try {
      await this.wdOauth2HttpService.put(
        `${this.wdLocalSharedFunctions.orderApiUrl}orders/${index}?idType=indexId`,
        {
          shippingOptionId: option,
        },
      );
      // Success Message
      this.wdErrorService.userSuccess(
        this.wdTranslate.instant('labels.success'),
        this.wdTranslate.instant('outputs.successfullyUpdated', {
          type: this.wdTranslate.instant('order.shipping.option.title'),
        }),
      );
    } catch (error) {
      // Error Message
      this.wdErrorService.userError(
        this.wdTranslate.instant('labels.error'),
        this.wdTranslate.instant('outputs.errorUpdating', {
          type: this.wdTranslate.instant('order.shipping.option.title'),
        }),
      );
    } finally {
      // Toggle Loader
      this.isUpdatingOrder.next(false);
    }
  }

  /**
   * PUT Updates shipping status of an order
   *
   * @since 4.71.0
   * @memberof WdOrdersV2Service
   */
  async updateShippingStatus(index: string, status: number): Promise<void> {
    // Toggle Loader
    this.isUpdatingOrder.next(true);
    try {
      await this.wdOauth2HttpService.put(
        `${this.wdLocalSharedFunctions.orderApiUrl}orders/${index}?idType=indexId`,
        {
          shippingStatusId: status,
        },
      );
      // Success Message
      this.wdErrorService.userSuccess(
        this.wdTranslate.instant('labels.success'),
        this.wdTranslate.instant('outputs.successfullyUpdated', {
          type: this.wdTranslate.instant('order.status.shipping.title'),
        }),
      );
    } catch (error) {
      // Error Message
      this.wdErrorService.userError(
        this.wdTranslate.instant('labels.error'),
        this.wdTranslate.instant('outputs.errorUpdating', {
          type: this.wdTranslate.instant('order.status.shipping.title'),
        }),
      );
    } finally {
      // Toggle Loader
      this.isUpdatingOrder.next(false);
    }
  }

  /**
   * PUT Updates shipping loading zone of an order
   *
   * @since 4.71.0
   * @memberof WdOrdersV2Service
   */
  async updateShippingLoadingZone(
    index: string,
    option: number,
  ): Promise<void> {
    // Toggle Loader
    this.isUpdatingOrder.next(true);
    try {
      await this.wdOauth2HttpService.put(
        `${this.wdLocalSharedFunctions.orderApiUrl}orders/${index}?idType=indexId`,
        {
          loadingZoneId: option ? option : null,
        },
      );
      // Success Message
      this.wdErrorService.userSuccess(
        this.wdTranslate.instant('labels.success'),
        this.wdTranslate.instant('outputs.successfullyUpdated', {
          type: this.wdTranslate.instant('order.shipping.loadingZone.title'),
        }),
      );
    } catch (error) {
      // Error Message
      this.wdErrorService.userError(
        this.wdTranslate.instant('labels.error'),
        this.wdTranslate.instant('outputs.errorUpdating', {
          type: this.wdTranslate.instant('order.shipping.loadingZone.title'),
        }),
      );
    } finally {
      // Toggle Loader
      this.isUpdatingOrder.next(false);
    }
  }

  /************************************************* class functions **************************************************/

  /**
   * Get Order Downloads
   * @since 4.71.0
   * @memberof WdOrdersV2Service
   */
  public getOrderDownloads(index: string): Array<IOrderExportModel> {
    const results = [];
    // CAM Export
    if (this.wdEnvironmentService.permissions.canViewCamductExport) {
      results.push({
        icon: 'download',
        label: this.translate.instant('exports.camExport'),
        target: '_blank',
        url: this.wdOrderReportService.getOrderReportUrl(index, 'cam'),
      });
    }
    return results;
  }

  /**
   * Get Order Exports
   * @since 4.71.0
   * @memberof WdOrdersV2Service
   */
  public getOrderExports(index: string): Array<IOrderExportModel> {
    const results = [];
    // Fulfillment PDF
    if (this.wdEnvironmentService.permissions.canViewFulfillmentPdfExport) {
      results.push({
        icon: 'pdf',
        label: this.translate.instant('exports.fulfillment_pdf'),
        target: '_blank',
        url: this.wdOrderReportService.getOrderReportUrl(index, 'fulfillment'),
      });
    }
    // Field PDF
    if (this.wdEnvironmentService.permissions.canViewFieldPdfExport) {
      results.push({
        icon: 'pdf',
        label: this.translate.instant('exports.field_pdf'),
        target: '_blank',
        url: this.wdOrderReportService.getOrderReportUrl(index, 'field'),
      });
    }
    // Field PDF
    if (this.wdEnvironmentService.permissions.canViewCombinedCsvExport) {
      results.push({
        icon: 'csv',
        label: this.translate.instant('exports.reports_csv'),
        target: '_blank',
        url: this.wdOrderReportService.getOrderReportUrl(index, 'csv'),
      });
    }
    return results;
  }

  /**
   * Get Order Reports
   * @since 4.71.0
   * @memberof WdOrdersV2Service
   */
  public getOrderReports(index: string): Array<IOrderExportModel> {
    const results = [];

    // BOM Report
    if (this.wdEnvironmentService.permissions.canViewWorkOrderBillingReport) {
      results.push({
        icon: 'reports',
        label: this.translate.instant('exports.bom_new'),
        target: '_blank',
        url: this.wdOrderReportService.getOrderReportUrl(
          index,
          'billingReport',
        ),
      });
    }

    // Supply Only Report
    if (
      this.wdEnvironmentService.permissions.canViewWorkOrderSupplyOnlyReport
    ) {
      results.push({
        icon: 'reports',
        label: this.translate.instant('exports.supply_new'),
        target: '_blank',
        url: this.wdOrderReportService.getOrderReportUrl(
          index,
          'supplyOnlyReport',
        ),
      });
    }

    // Shop Report
    if (
      this.wdEnvironmentService.permissions.canViewWorkOrderShopReportModern
    ) {
      results.push({
        icon: 'reports',
        label: this.translate.instant('exports.shop_new'),
        target: '_blank',
        url: this.wdOrderReportService.getOrderReportUrl(
          index,
          'shopReportModern',
        ),
      });
    }

    return results;
  }

  /**
   * Get Order Resources
   * @since 4.71.0
   * @memberof WdOrdersV2Service
   */
  public getOrderResources(number: string): any {
    const results: any = {};
    // Tool Tracker Order URL
    results.toolTrackerOrderUrl = this.wdEnvironmentService.resources.getToolTrackerOrderUrl(
      number,
    );
    return results;
  }

  /************************************************* class functions **************************************************/

  /**
   * Create instance of OrganizationFilterModel
   */
  buildFilterModel(
    filters: BehaviorSubject<IOrderFiltersModel>,
  ): OrderFilterModel {
    // Filters
    const {
      dateStart,
      dateEnd,
      dateType,
      lastId,
      orderType,
      jobFilter,
      orderNumberFilter,
      orderShippingTypes,
      orderStatuses,
      orderedForFilter,
      organizationFilter,
      productionStatuses,
      deliveryStatuses,
      fulfillmentGroups,
      seatTypes,
      userGroups,
    } = filters.value as any;
    // Page size limit
    const limit = this.orderViewSettings.pageSize;
    // Specify the includeLookups parameter as a comma-separated list of
    // data to be included in the orders (via $lookup in the order API).
    // Note: Currently, only 'vehicleAssignments' is supported (with required permissions).
    const includedLookups = this.wdEnvironmentService.permissions
      .canViewVehicleAssignments
      ? this.wdLocalSharedFunctions.allowedLookupTypeKey.VEHICLE_ASSIGNMENTS
      : null;
    // Order Filter Model
    return new OrderFilterModel(
      dateStart,
      dateEnd,
      dateType,
      lastId,
      limit,
      jobFilter,
      orderNumberFilter,
      orderType,
      orderShippingTypes,
      orderStatuses,
      orderedForFilter,
      organizationFilter,
      productionStatuses,
      deliveryStatuses,
      fulfillmentGroups,
      seatTypes,
      userGroups,
      includedLookups,
    );
  }

  /**
   * Get serialized order filters.
   * Builds order filter model then serialize results.
   */
  getSerializedOrderFilters(filters: BehaviorSubject<IOrderFiltersModel>) {
    const filterModel = this.buildFilterModel(filters);
    return filterModel.getSerialized(this.orderViewSettings);
  }

  /**
   * Build the URL using the currently set order filters to get Work Orders CSV export from data-export-api
   *
   * @since 4.71.0
   * @memberof WdOrdersV2Service
   */
  getWorkOrdersCsvUrl(): string {
    const accessToken = this.wdOauth2HttpService.getAccessToken();
    const serializedFilters = this.getSerializedOrderFilters(this.orderFilters);
    const params =
      this.wdLocalSharedFunctions.buildFilters(serializedFilters) +
      '&access_token=' +
      accessToken;
    const path = 'reports/orders/workOrders';
    return `${this.wdLocalSharedFunctions.dataExportApiUrl}${path}${
      path ? '?' + params : ''
    }`;
  }

  /**
   * Initialize Custom Tabs
   * @since 4.77.0
   * @memberof WdOrdersV2Service
   */
  initializeCustomTabs(orderType: string) {
    this.tabbedOrderContent.forEach((tab: IOrderTabs) => {
      if (tab.isCustom) {
        this.orderViewSettings.selected.columns[orderType][
          tab.key
        ] = tab.columns.map((column: any) => {
          return column.id;
        });
      }
    });
  }

  /**
   * Initialize Local Storage Settings
   * @since 4.71.0
   * @memberof WdOrdersV2Service
   */
  initializeLocalStorage() {
    // Initialize view settings based on saved local storage settings
    if (this.wdStorageService[this.localStorageKey]) {
      this.orderViewSettings = this.wdStorageService[this.localStorageKey];
    }
    // Initialize default settings from local storage
    this.wdStorageService.initializeDefaults(
      this.localStorageKey,
      this.orderViewSettings,
    );
    this.orderViewSettings = this.wdStorageService[this.localStorageKey];
  }

  /**
   * Initialize Order Columns
   */
  initColumnToggleOptions(
    orderType: string,
    selectedTab: string = this.orderViewSettings.selectedTab?.key,
  ) {
    // Initialize Tabs
    this.initializeCustomTabs(orderType);
    // Selected columns from local storage
    const savedColumns: Array<string> = this.orderViewSettings.selected.columns[
      orderType
    ][selectedTab];

    // Initialize an empty array to hold column options
    const options: Array<any> = [];

    // Get custom tab keys
    const customTabKeys = this.tabbedOrderContent
      .filter((tab: IOrderTabs) => {
        return tab.isCustom;
      })
      .map((tab: IOrderTabs) => {
        return tab.key;
      });

    // Define arrays containing different tab names
    const showOnAllTabs = ['all'];
    const showOnDetailsTab = customTabKeys.concat(['details']);
    const showOnProductionTab = customTabKeys.concat(['production']);
    const showOnFulfillmentTab = customTabKeys.concat(['fulfillment']);
    const showOnProductionAndFulfillmentTab = showOnProductionTab.concat(
      showOnFulfillmentTab,
    );
    const showOnShippingTab = customTabKeys.concat(['shipping']);
    const showOnWorkAndLookaheadOrders = ['work', 'lookahead'];
    const showOnWorkOrders = ['work'];
    const showOnLookaheadOrders = ['lookahead'];

    // Helper function to simplify the creation of option objects
    function createOption(
      key: string,
      label: string,
      visibility: any,
      canToggle: boolean,
    ) {
      return { key, label, visibility, canToggle };
    }

    // Push details column options
    options.push(
      // General options
      createOption(
        'select',
        '',
        {
          orderTypes: showOnWorkAndLookaheadOrders,
          selectedTabs: showOnAllTabs,
        },
        false,
      ),
      createOption(
        'number',
        '',
        {
          orderTypes: showOnWorkAndLookaheadOrders,
          selectedTabs: showOnAllTabs,
        },
        false,
      ),
      createOption(
        'dateOrdered',
        this.translate.instant('order.columns.date.ordered.title'),
        {
          orderTypes: showOnWorkAndLookaheadOrders,
          selectedTabs: showOnAllTabs,
        },
        false,
      ),
      createOption(
        'dateRequested',
        this.translate.instant('order.columns.date.requested.title'),
        {
          orderTypes: showOnWorkAndLookaheadOrders,
          selectedTabs: showOnDetailsTab,
        },
        true,
      ),
      createOption(
        'lookahead',
        this.translate.instant('order.columns.lookahead.title'),
        { orderTypes: showOnLookaheadOrders, selectedTabs: showOnDetailsTab },
        true,
      ),
      createOption(
        'jobNumber',
        this.translate.instant('order.columns.job.number.title'),
        {
          orderTypes: showOnWorkAndLookaheadOrders,
          selectedTabs: showOnDetailsTab,
        },
        true,
      ),
      createOption(
        'jobName',
        this.translate.instant('order.columns.job.name.title'),
        {
          orderTypes: showOnWorkAndLookaheadOrders,
          selectedTabs: showOnDetailsTab,
        },
        true,
      ),
      createOption(
        'jobPhase',
        this.translate.instant('order.columns.job.phase.title'),
        {
          orderTypes: showOnWorkAndLookaheadOrders,
          selectedTabs: showOnDetailsTab,
        },
        true,
      ),
      createOption(
        'orderedBy',
        this.translate.instant('order.columns.ordered.by.title'),
        {
          orderTypes: showOnWorkAndLookaheadOrders,
          selectedTabs: showOnDetailsTab,
        },
        true,
      ),
      createOption(
        'orderedFor',
        this.translate.instant('order.columns.ordered.for.title'),
        {
          orderTypes: showOnWorkAndLookaheadOrders,
          selectedTabs: showOnDetailsTab,
        },
        true,
      ),
      createOption(
        'userGroup',
        this.translate.instant('order.columns.user.group.title'),
        {
          orderTypes: showOnWorkAndLookaheadOrders,
          selectedTabs: showOnDetailsTab,
        },
        true,
      ),
      createOption(
        'poNumber',
        this.translate.instant('order.columns.poNumber.title'),
        {
          orderTypes: showOnWorkAndLookaheadOrders,
          selectedTabs: showOnDetailsTab,
        },
        true,
      ),
      createOption(
        'tag',
        this.translate.instant('order.columns.tag.title'),
        { orderTypes: showOnWorkOrders, selectedTabs: showOnDetailsTab },
        true,
      ),
      createOption(
        'specialInstructions',
        this.translate.instant('order.columns.specialInstructions.title'),
        {
          orderTypes: showOnWorkAndLookaheadOrders,
          selectedTabs: showOnDetailsTab,
        },
        true,
      ),
    );

    // Push production column options
    options.push(
      createOption(
        'dateProductionStart',
        this.translate.instant('order.columns.date.production.start.title'),
        {
          orderTypes: showOnWorkAndLookaheadOrders,
          selectedTabs: showOnProductionTab,
        },
        true,
      ),
      createOption(
        'dateProductionEnd',
        this.translate.instant('order.columns.date.production.end.title'),
        {
          orderTypes: showOnWorkAndLookaheadOrders,
          selectedTabs: showOnProductionTab,
        },
        true,
      ),
    );
    // Only add weight if you have permission to view it
    if (this.wdEnvironmentService.permissions.canViewWeight) {
      options.push(
        createOption(
          'weight',
          this.translate.instant('order.columns.weight.title'),
          {
            orderTypes: showOnWorkAndLookaheadOrders,
            selectedTabs: showOnProductionTab,
          },
          true,
        ),
      );
    }
    // Only add price if you have permission to view it
    if (this.wdEnvironmentService.permissions.canViewPricing) {
      options.push(
        createOption(
          'price',
          this.translate.instant('order.columns.price.title'),
          {
            orderTypes: showOnWorkAndLookaheadOrders,
            selectedTabs: showOnProductionTab,
          },
          true,
        ),
      );
    }
    // Only add shop labour if you have permission to view it
    if (this.wdEnvironmentService.permissions.canViewFabricationTime) {
      options.push(
        createOption(
          'labour',
          this.translate.instant('order.columns.labour.title'),
          {
            orderTypes: showOnWorkAndLookaheadOrders,
            selectedTabs: showOnProductionTab,
          },
          true,
        ),
      );
    }

    options.push(
      createOption(
        'items',
        this.translate.instant('order.columns.items.title'),
        {
          orderTypes: showOnWorkAndLookaheadOrders,
          selectedTabs: showOnProductionTab,
        },
        true,
      ),
      createOption(
        'itemsProgress',
        this.translate.instant('order.columns.items.progress.title'),
        {
          orderTypes: showOnWorkAndLookaheadOrders,
          selectedTabs: showOnProductionTab,
        },
        true,
      ),
      createOption(
        'productionStatus',
        this.translate.instant('order.columns.status.production.title'),
        {
          orderTypes: showOnWorkAndLookaheadOrders,
          selectedTabs: showOnProductionAndFulfillmentTab,
        },
        true,
      ),
    );

    // Push fulfillment group column options
    this.orderOptions.value.orderFulfillmentGroups?.forEach((group: any) => {
      options.push(
        createOption(
          this.fulfillmentColumnPrefix + group.index + '-' + group.code,
          group.label,
          {
            orderTypes: showOnWorkAndLookaheadOrders,
            selectedTabs: showOnFulfillmentTab,
          },
          true,
        ),
      );
    });

    // Push shipping-related column options
    options.push(
      createOption(
        'labourProgress',
        this.translate.instant('order.columns.labour.progress.title'),
        {
          orderTypes: showOnWorkAndLookaheadOrders,
          selectedTabs: showOnFulfillmentTab,
        },
        true,
      ),
    );

    // Push shipping-related column options
    options.push(
      createOption(
        'dateShipping',
        this.translate.instant('order.columns.date.shipping.title'),
        {
          orderTypes: showOnWorkAndLookaheadOrders,
          selectedTabs: showOnShippingTab,
        },
        true,
      ),
      createOption(
        'shippingWindowStart',
        this.translate.instant('order.columns.shipping.start.title'),
        {
          orderTypes: showOnWorkAndLookaheadOrders,
          selectedTabs: showOnShippingTab,
        },
        true,
      ),
      createOption(
        'shippingWindowEnd',
        this.translate.instant('order.columns.shipping.end.title'),
        {
          orderTypes: showOnWorkAndLookaheadOrders,
          selectedTabs: showOnShippingTab,
        },
        true,
      ),
      createOption(
        'shippingOption',
        this.translate.instant('order.columns.shipping.option.title'),
        {
          orderTypes: showOnWorkAndLookaheadOrders,
          selectedTabs: showOnShippingTab,
        },
        true,
      ),
      createOption(
        'shippingAddress',
        this.translate.instant('order.columns.shipping.address.title'),
        {
          orderTypes: showOnWorkAndLookaheadOrders,
          selectedTabs: showOnShippingTab,
        },
        true,
      ),
    );
    // Only add assigned vehicles column if we have permission
    if (this.wdEnvironmentService.permissions.canViewVehicleAssignments) {
      options.push(
        createOption(
          'shippingAssignments',
          this.translate.instant('order.columns.shipping.assignments.title'),
          {
            orderTypes: showOnWorkAndLookaheadOrders,
            selectedTabs: showOnShippingTab,
          },
          true,
        ),
      );
    }
    options.push(
      createOption(
        'loadingZoneOption',
        this.translate.instant('order.columns.shipping.loadingZone.title'),
        {
          orderTypes: showOnWorkAndLookaheadOrders,
          selectedTabs: showOnShippingTab,
        },
        true,
      ),
      createOption(
        'shippingStatus',
        this.translate.instant('order.columns.status.shipping.title'),
        {
          orderTypes: showOnWorkAndLookaheadOrders,
          selectedTabs: showOnShippingTab,
        },
        true,
      ),
      createOption(
        'orderMessages',
        this.translate.instant('order.columns.messages.title'),
        {
          orderTypes: showOnWorkAndLookaheadOrders,
          selectedTabs: showOnAllTabs,
        },
        false,
      ),
      createOption(
        'orderAttachments',
        this.translate.instant('order.columns.attachments.title'),
        {
          orderTypes: showOnWorkAndLookaheadOrders,
          selectedTabs: showOnAllTabs,
        },
        false,
      ),
      createOption(
        'orderExports',
        this.translate.instant('order.columns.exports.title'),
        {
          orderTypes: showOnWorkAndLookaheadOrders,
          selectedTabs: showOnAllTabs,
        },
        false,
      ),
      createOption(
        'orderStatus',
        this.translate.instant('order.columns.status.title'),
        {
          orderTypes: showOnWorkAndLookaheadOrders,
          selectedTabs: showOnAllTabs,
        },
        false,
      ),
    );

    // Set all column options
    this.allColumnOptions = this.toggleOrderColumnSelections(
      orderType,
      options,
    );
    // Set options for column toggling
    this.orderListColumnsOptions = this.toggleOrderColumnSelections(
      orderType,
      options,
      savedColumns ? savedColumns : [],
      selectedTab,
    );
    // Set visible columns for order list
    this.setVisibleOrderColumns(orderType, selectedTab);
  }

  /**
   * Initialize view settings based on route query params
   * @since 4.71.0
   * @memberof WdOrdersV2Service
   */
  initializeRouteParams() {
    this.route.queryParams.subscribe((params) => {
      if (params) {
        // Set Page
        this.orderViewSettings.page = params.page
          ? parseInt(params.page)
          : this.orderViewSettings.page;
        // Page Size
        this.orderViewSettings.pageSize = this.wdLocalSharedFunctions.pageSizeOptions.includes(
          parseInt(params.pageSize),
        )
          ? parseInt(params.pageSize)
          : this.orderViewSettings.pageSize;
        // Sort Key
        this.orderViewSettings.sortKey = params.sortKey
          ? params.sortKey
          : this.orderViewSettings.sortKey;
        // Sort Direction
        this.orderViewSettings.sortDir = this.wdLocalSharedFunctions.pageSortDirectionOptions.includes(
          params.sortDir,
        )
          ? params.sortDir
          : this.orderViewSettings.sortDir;
        // Set Tab
        const tabKey = params.tab
          ? params.tab
          : this.orderViewSettings.selectedTab?.key;
        const firstVisibleTab = this.tabbedOrderContent.filter(
          (tab: IOrderTabs) => tab.isVisible,
        )[0];
        const requestedTab = this.tabbedOrderContent.find((tab: IOrderTabs) => {
          return tab.key === tabKey;
        });
        // If there are no visible tabs, toggle requested tab to visible
        if (!firstVisibleTab) {
          requestedTab.isVisible = true;
        }
        this.orderViewSettings.selectedTab =
          tabKey && requestedTab && requestedTab.isVisible
            ? requestedTab
            : firstVisibleTab;
      }
    });
  }

  /**
   * Triggered when live filters are changed
   * Prevents the API being called with every keystroke
   * @since 4.71.0
   * @memberof WdOrdersV2Service
   */
  liveFilterChanged(key: string, value: string): void {
    const isDateTypeFilter = key === 'dateType';
    const isDateRangeFilter =
      key === 'dateStart' || key === 'dateEnd' || key === 'dateType';
    const isOrderNumberSearch = key === 'orderNumberFilter';
    const filterValues: any = this.orderFilters.getValue();
    const currentLiveFilters = this.currentLiveFilters;
    // Set search delay for order number search
    const searchDelay = isOrderNumberSearch ? 1200 : 0;
    let doSearch = true;
    setTimeout(() => {
      // Ensures date range filter is only performed once all three required inputs are set
      if (value !== filterValues[key] && isOrderNumberSearch) {
        doSearch = false;
      }
      // Restrict date range filtering until all three filters are set
      // Reload orders if all three filters are cleared
      if (isDateRangeFilter) {
        if (isDateTypeFilter && !filterValues.dateType) {
          filterValues.dateStart = undefined;
          currentLiveFilters.dateStart = undefined;
          filterValues.dateEnd = undefined;
          currentLiveFilters.dateEnd = undefined;
        }
        doSearch =
          (filterValues.dateType &&
            filterValues.dateStart &&
            filterValues.dateEnd) ||
          (isDateTypeFilter &&
            !filterValues.dateType &&
            !filterValues.dateStart &&
            !filterValues.dateEnd);
      }
      // Reload orders
      if (doSearch) {
        const oldValueJSON = this.currentLiveFilters[key];
        const newValueJSON = JSON.stringify(filterValues[key]);
        if (oldValueJSON !== newValueJSON) {
          void this.fetchOrders(false);
        }
      }
      // Set live filter value
      currentLiveFilters[key] = JSON.stringify(value);
    }, searchDelay);
  }

  /**
   * Set assignments labels.
   */
  setAssignmentLabels(
    assignments: Array<IVehicleAssignmentModel>,
  ): Array<IVehicleAssignmentModel> {
    return assignments.map((assignment: IVehicleAssignmentModel) => {
      const vehicleLabel = assignment.vehicle?.label ?? '';
      const driverFirstName = assignment.driver?.firstName ?? '';
      const driverLastName = assignment.driver?.lastName ?? '';
      const driverName =
        driverFirstName || driverLastName
          ? `${driverFirstName} ${driverLastName}`.trim()
          : '';
      const dateRange = `${this.wdDateTimeHelper.getDateAsString(
        assignment.date.start,
        'Do MMM',
      )} - ${this.wdDateTimeHelper.getDateAsString(
        assignment.date.end,
        'Do MMM',
      )}`;
      assignment.label = `${vehicleLabel} - ${driverName} (${dateRange})`;
      assignment.labelShort = `${vehicleLabel}`;
      return assignment;
    });
  }

  /**
   * Parse Filters
   * @since 4.71.0
   * @memberof WdOrdersV2Service
   */
  parseFilters(key: string, values: Array<any>) {
    return values.map((status) => {
      let targetKey = 'index';
      let selectedIds: Array<string | number> = [];

      // Order Shipping Types
      if (key === 'orderShippingTypes') {
        targetKey = 'value';
        selectedIds = this.orderViewSettings.selected.types.shipping.values;
      }

      // Order Statuses
      if (key === 'orderStatuses') {
        targetKey = 'id';
        selectedIds = this.orderViewSettings.selected.statuses.order.ids;
        // @todo Investigate saving types (used for icons) and removing hard coded values
        status.type = status?.id;
      }

      // Delivery Statuses
      if (key === 'orderDeliveryStatuses') {
        selectedIds = this.orderViewSettings.selected.statuses.shipping.indexes;
        status.type = WdOrderStatusService.getDeliveryTypeByStatus(status);
      }

      // Fulfillment Groups
      if (key === 'orderFulfillmentGroups') {
        selectedIds = this.orderViewSettings.selected.groups.fulfillment
          .indexes;
      }

      // Production Statuses
      if (key === 'orderFulfillmentStatuses') {
        selectedIds = this.orderViewSettings.selected.statuses.production
          .indexes;
        status.label = status?.code; // Set label to be same as code
        status.type = WdOrderStatusService.getProductionTypeByStatus(status);
      }

      // Seat Types
      if (key === 'seatTypes') {
        targetKey = 'bcxId';
        selectedIds = this.orderViewSettings.selected.types.seat.values;
      }

      // User Groups
      if (key === 'userGroups') {
        targetKey = 'id';
        selectedIds = this.orderViewSettings.selected.groups.user.ids;
      }

      // Users
      if (key === 'users') {
        status.fullName = [status.firstName, status.lastName].join(' ');
      }

      // Set selected status based on local storage values
      status.selected = selectedIds
        ? selectedIds.includes(status[targetKey])
        : true;

      return status;
    });
  }

  /**
   * Parse Order
   *
   * @since 4.71.0
   * @memberof WdOrdersV2Service
   */
  parseOrder(order: IOrderModel): object {
    const result = order;
    const orderFilterOptions = this.orderFilters?.value; // Order Filter Options
    // Totals
    if (result.totals) {
      // Shop Labour
      const fabricationTotal = this.wdSharedFunctionsService.getDescendantProperty(
        result,
        'totals.shopLabour.value',
      );
      const fabricationRemaining = this.wdSharedFunctionsService.getDescendantProperty(
        result,
        'totals.shopLabourRemain.value',
      );
      // @todo Fabrication remaining needs to be fixed to 3 decimal places API side, the same as total (or calculate API side?)
      const fabricationComplete =
        fabricationTotal - fabricationRemaining.toFixed(3);

      // Weight
      const weightTotal = this.wdSharedFunctionsService.getDescendantProperty(
        result,
        'totals.weight.value',
      );
      // Set Values
      result.totals.shopLabour.label = WdLocalSharedFunctionsService.convertFloatHoursToHoursAndMinutes(
        fabricationTotal,
        'short',
      );
      result.totals.shopLabourComplete = {
        value: fabricationComplete,
        label: WdLocalSharedFunctionsService.convertFloatHoursToHoursAndMinutes(
          fabricationComplete,
          'short',
        ),
      };
      result.totals.shopLabourRemain.label = WdLocalSharedFunctionsService.convertFloatHoursToHoursAndMinutes(
        fabricationRemaining,
        'short',
      );
      result.totals.weight.label = [
        weightTotal.toFixed(2),
        this.translate.instant(
          this.wdLocalSharedFunctions.translationKeys.weightUnit,
        ),
      ].join(' ');

      result.totals.price.label =
        this.translate.instant(
          this.wdLocalSharedFunctions.translationKeys.currencySymbol,
        ) + result.totals.price.value;
    }

    // Get dates
    const orderedDateStr = this.wdSharedFunctionsService.getDescendantProperty(
      result,
      'ordered.date',
    );
    const requestedDateStr = this.wdSharedFunctionsService.getDescendantProperty(
      result,
      'requested.date',
    );
    const productionStartDateStr = this.wdSharedFunctionsService.getDescendantProperty(
      result,
      'production.date',
    );
    const productionEndDateStr = this.wdSharedFunctionsService.getDescendantProperty(
      result,
      'production.endDate',
    );
    const shippingDateStr = this.wdSharedFunctionsService.getDescendantProperty(
      result,
      'delivery.date',
    );

    const dateOrdered = orderedDateStr ? new Date(orderedDateStr) : null;
    const dateRequested = requestedDateStr ? new Date(requestedDateStr) : null;
    const dateProductionStart = productionStartDateStr
      ? new Date(productionStartDateStr)
      : null;
    const dateProductionEnd = productionEndDateStr
      ? new Date(productionEndDateStr)
      : new Date(productionStartDateStr);
    const dateShipping = shippingDateStr ? new Date(shippingDateStr) : null;

    // Dates
    if (result.ordered) {
      result.ordered.date = new Date(dateOrdered);
    }
    if (result.requested) {
      result.requested.date = new Date(dateRequested);
    }
    if (result.production) {
      result.production = {
        // Start date
        start: {
          date: dateProductionStart,
          min: dateOrdered,
          max: dateProductionEnd,
        },
        // End date
        end: {
          date: dateProductionEnd,
          min: dateProductionStart,
          max: dateShipping,
        },
        status: result.production.status,
      };
    }
    if (result.delivery) {
      result.delivery.start = {
        date: new Date(dateShipping),
        min: new Date(dateProductionEnd),
      };
      result.delivery.time = {
        start: result.delivery.time?.start
          ? new Date(result.delivery.time?.start)
          : null,
        end: result.delivery.time?.end
          ? new Date(result.delivery.time?.end)
          : null,
      };

      if (result.delivery.address) {
        result.delivery.address.label = [
          result.delivery?.address?.company,
          result.delivery?.address?.street,
          result.delivery?.address?.city,
          result.delivery?.address?.state?.label,
          result.delivery?.address?.country?.label,
          result.delivery?.address?.zipcode,
          result.delivery?.address?.phone,
        ]
          .filter(Boolean)
          .join(', ');
      }
      if (result.delivery.vehicle) {
        // Get all assignments
        const allAssignments: Array<IVehicleAssignmentModel> =
          this.orderOptions.value.orderShippingVehicleAssignments || [];
        // Get valid vehicle assignments for each order based on order shipping date
        const options: Array<IVehicleAssignmentModel> = this.wdLocalSharedFunctions.getAssignmentsByShippingDate(
          allAssignments,
          new Date(order.delivery?.date),
        );
        // Get currently selected vehicle assignment object
        const selectedAssignments: Array<IVehicleAssignmentModel> = this.wdLocalSharedFunctions.getAssignmentsByIds(
          allAssignments,
          result.delivery?.vehicle?.assignments.map(
            (assignment: IVehicleAssignmentModel) => assignment.id,
          ),
        );
        result.delivery.vehicle.assignments = this.setAssignmentLabels(
          selectedAssignments,
        );
        result.delivery.vehicle.assignmentOptions = [
          ...this.setAssignmentLabels(options),
        ];
      }
    }

    // Set labels (user and jobs)
    this.setOrderLabels(result);

    // Overall Status
    if (result.status) {
      const statusId = result.status.id;
      const options = orderFilterOptions?.orderStatuses
        ? orderFilterOptions?.orderStatuses
        : [];
      const match = this.wdSharedFunctionsService.getArrayObjectByKey(
        statusId,
        'id',
        options,
      );
      // Set Status and Flag for Missing Status
      result.status = options && !!match?.id ? match : result?.status;
      result.status.hasMatchingStatusOption =
        options.indexOf(result?.status) > -1;
    }

    // Production Status
    if (result.production && result.production?.status) {
      const statusIndex = result.production?.status?.index;
      const options = orderFilterOptions?.productionStatuses
        ? orderFilterOptions?.productionStatuses
        : [];
      const match = this.wdSharedFunctionsService.getArrayObjectByKey(
        statusIndex,
        'index',
        options,
      );
      // Set Status and Flag for Missing Status
      result.production.status =
        options && !!match?.index ? match : result.production?.status;
      result.production.status.hasMatchingStatusOption =
        options.indexOf(result.production?.status) > -1;
    }

    // Shipping
    if (result.delivery) {
      // Status
      if (result.delivery?.status) {
        const statusIndex = result.delivery?.status?.index;
        const options = orderFilterOptions?.deliveryStatuses
          ? orderFilterOptions?.deliveryStatuses
          : [];
        const match = this.wdSharedFunctionsService.getArrayObjectByKey(
          statusIndex,
          'index',
          options,
        );
        // Set Status and Flag for Missing Status
        result.delivery.status =
          options && !!match?.index ? match : result.delivery?.status;
        result.delivery.status.hasMatchingStatusOption =
          options.indexOf(result.delivery?.status) > -1;
      }
      // Option
      if (result.delivery?.option) {
        const optionValue = result.delivery?.option;
        const options = orderFilterOptions?.orderShippingTypes
          ? orderFilterOptions?.orderShippingTypes
          : [];
        // Set Option
        result.delivery.option = this.wdSharedFunctionsService.getArrayObjectByKey(
          optionValue,
          'value',
          options,
        );
      }
    }

    // Fulfillment
    if (result.fulfillment?.groups) {
      // Groups
      const groups = result.fulfillment?.groups;

      // Selected Fulfillment Groups
      const fgColumnSelectedFgs = this.wdSharedFunctionsService.getDescendantProperty(
        this.orderViewSettings,
        'selected.groups.fulfillment.indexes',
      );
      // Selected visible groups count for order
      let visibleSelectedFgs = 0;
      // Loop Groups
      groups.forEach((group: any) => {
        // Set Group Module
        group.module = this.wdSharedFunctionsService.getArrayObjectByKey(
          group.index,
          'index',
          orderFilterOptions?.fulfillmentGroups,
        )?.module;
        if (group?.status?.index) {
          const statusIndex = group?.status?.index;
          const options = orderFilterOptions?.productionStatuses
            ? orderFilterOptions?.productionStatuses
            : [];
          const match = this.wdSharedFunctionsService.getArrayObjectByKey(
            statusIndex,
            'index',
            options,
          );
          // Only increase visible selected fulfillment groups
          if (fgColumnSelectedFgs) {
            if (fgColumnSelectedFgs.includes(group.index)) {
              visibleSelectedFgs++;
            }
          } else {
            visibleSelectedFgs++;
          }
          // Set Status and Flag for Missing Status
          group.status = options && !!match?.index ? match : group.status;
          group.status.hasMatchingStatusOption =
            options.indexOf(group.status) > -1;
        }
      });
      // Selected visible groups count for order
      result.visibleSelectedFgs = visibleSelectedFgs;
    }

    const canJobEditOverride: boolean =
      result.job?.override &&
      result.type === 'ecommerce' &&
      !!this.wdEnvironmentService.permissions.canEditEcommerceJob;
    result.settings = {
      canEditEcomJobOverride: canJobEditOverride,
    };

    // Model View Link
    result.modelLink = this.wdLocalSharedFunctions.getModelViewerUrl(
      order.index,
    );

    // Downloads, Exports, Reports and Resources
    result.downloads = this.getOrderDownloads(order.index);
    result.exports = this.getOrderExports(order.index);
    result.reports = this.getOrderReports(order.index);
    result.resources = this.getOrderResources(order.number);

    // Messages and attachments
    const msgCount = this.wdSharedFunctionsService.getDescendantProperty(
      order,
      'meta.messages.count',
    );
    result.messages = msgCount ? msgCount : 0;
    if (result.attachments && result.attachments.length) {
      this.parseOrderAttachments(result.attachments);
    }

    return result;
  }

  /**
   * Parse Order Attachments
   * - add access token to the attachment URLs
   *
   * @since 4.71.0
   * @memberof WdOrdersV2Service
   */
  parseOrderAttachments(attachments: Array<any>) {
    const orderApiUrl = this.wdEnvironmentService.configuration.getOrderApiPath;

    for (const attachment of attachments) {
      // Only continue if the attachment url is calling orderApi
      if (attachment.url && attachment.url.includes(orderApiUrl)) {
        // Add Access Token to Params
        const accessToken = this.wdOauth2HttpService.getAccessToken();
        attachment.url += '&access_token=' + accessToken;
      }
    }
  }

  /**
   * Get Orders Data from Response
   *
   * @since 4.71.0
   * @param inputData
   * @memberof WdOrdersV2Service
   */
  parseOrders(inputData: { orders: Array<IOrderModel> }): Array<any> {
    let orders: any = [];

    if (!isEmpty(inputData)) {
      if (!isEmpty(inputData.orders)) {
        orders = inputData.orders.map((order: any) => {
          return this.parseOrder(order);
        });
        // Get the highest count for non-empty fulfillment groups
        // Used for dynamic column width when hiding empty fulfillment groups
        this.orderFulfillmentCountMax = Math.max(
          ...orders.map((o: any) => {
            return o.visibleSelectedFgs;
          }),
        );
      }
    }

    return orders;
  }

  /**
   * Save Selected Order Filters to Local Storage
   *
   * @since 4.71.0
   * @memberof WdOrdersV2Service
   */
  saveSelectedOrderFilters() {
    // All Filter Values
    const orderFilters = this.orderFilters.value;

    // Selected Filter Settings
    const selectedFilterSettings = this.orderViewSettings.selected;

    // Set Last Known Totals (used on initial data load to check which filters are needed)
    selectedFilterSettings.groups.fulfillment.total =
      orderFilters.fulfillmentGroups?.length;
    selectedFilterSettings.groups.user.total = orderFilters.userGroups?.length;
    selectedFilterSettings.statuses.shipping.total =
      orderFilters.deliveryStatuses?.length;
    selectedFilterSettings.statuses.order.total =
      orderFilters.orderStatuses?.length;
    selectedFilterSettings.statuses.production.total =
      orderFilters.productionStatuses?.length;
    selectedFilterSettings.types.shipping.total =
      orderFilters.orderShippingTypes?.length;
    selectedFilterSettings.types.seat.total = orderFilters.seatTypes?.length;

    // Set Selected Groups / Statuses / Shipping Types
    selectedFilterSettings.groups.fulfillment.indexes = this.wdLocalSharedFunctions.getSelectedFiltersValuesByKey(
      orderFilters.fulfillmentGroups,
      'index',
    );
    selectedFilterSettings.groups.user.ids = this.wdLocalSharedFunctions.getSelectedFiltersValuesByKey(
      orderFilters.userGroups,
      'id',
    );
    selectedFilterSettings.statuses.shipping.indexes = this.wdLocalSharedFunctions.getSelectedFiltersValuesByKey(
      orderFilters.deliveryStatuses,
      'index',
    );
    selectedFilterSettings.statuses.order.ids = this.wdLocalSharedFunctions.getSelectedFiltersValuesByKey(
      orderFilters.orderStatuses,
      'id',
    );
    selectedFilterSettings.statuses.production.indexes = this.wdLocalSharedFunctions.getSelectedFiltersValuesByKey(
      orderFilters.productionStatuses,
      'index',
    );
    selectedFilterSettings.types.shipping.values = this.wdLocalSharedFunctions.getSelectedFiltersValuesByKey(
      orderFilters.orderShippingTypes,
      'value',
    );
    selectedFilterSettings.types.seat.values = this.wdLocalSharedFunctions.getSelectedFiltersValuesByKey(
      orderFilters.seatTypes,
      'bcxId',
    );

    // Save Local Storage Settings
    this.saveLocalStorageSettings();
  }

  /**
   * Save Local Storage Settings
   * @since 4.71.0
   * @memberof WdOrdersV2Service
   */
  saveLocalStorageSettings() {
    this.wdStorageService.saveLocalStorageConfiguration(
      this.localStorageKey,
      this.orderViewSettings,
    );
  }

  /**
   * Get Filters Data from Response
   *
   * @since 4.71.0
   * @memberof WdOrdersV2Service
   */
  setFiltersData(inputData?: any): void {
    let orderDeliveryStatuses: any = [];
    let orderFulfillmentGroups: any = [];
    let orderOrderStatuses: any = [];
    let orderOrganizations: any = [];
    let orderProductionStatuses: any = [];
    let orderShippingTypes: any = [];
    let orderSeatTypes: any = [];
    let orderUserGroups: any = [];
    let orderUsers: any = [];

    if (!isEmpty(inputData)) {
      if (!isEmpty(inputData.seatTypes)) {
        orderSeatTypes = this.parseFilters('seatTypes', inputData.seatTypes);
      }
      if (!isEmpty(inputData.orderShippingTypes)) {
        // Active shipping types
        const activeShippingTypes: Array<any> = this.wdOrderShippingOptionsService.getActiveShippingOptions(
          inputData.orderShippingTypes,
        );
        // Archived shipping types
        const archivedShippingTypes: Array<any> = this.wdOrderShippingOptionsService.getArchivedShippingOptions(
          inputData.orderShippingTypes,
        );
        // Allow filtering of archived shipping types
        if (archivedShippingTypes.length > 0) {
          const archivedStatusesOption = {
            label: this.translate.instant(
              'order.filters.shipping.types.archived',
            ),
            value: archivedShippingTypes
              .map((type: any) => type.value)
              .join(','),
            sort: 0,
            status: this.wdOrderShippingOptionsService
              .orderShippingTypeStatusKey.ACTIVE,
          };
          activeShippingTypes.push(archivedStatusesOption);
        }
        orderShippingTypes = this.parseFilters(
          'orderShippingTypes',
          activeShippingTypes,
        );
      }
      if (!isEmpty(inputData.orderStatuses)) {
        orderOrderStatuses = this.parseFilters(
          'orderStatuses',
          inputData.orderStatuses,
        );
      }
      if (!isEmpty(inputData.orderDeliveryStatuses)) {
        orderDeliveryStatuses = this.parseFilters(
          'orderDeliveryStatuses',
          inputData.orderDeliveryStatuses,
        );
      }
      if (!isEmpty(inputData.orderFulfillmentGroups)) {
        orderFulfillmentGroups = this.parseFilters(
          'orderFulfillmentGroups',
          inputData.orderFulfillmentGroups,
        );
      }
      if (!isEmpty(inputData.orderFulfillmentStatuses)) {
        orderProductionStatuses = this.parseFilters(
          'orderFulfillmentStatuses',
          inputData.orderFulfillmentStatuses,
        );
      }
      if (!isEmpty(inputData.organizations)) {
        orderOrganizations = this.parseFilters(
          'organizations',
          inputData.organizations,
        );
      }
      if (!isEmpty(inputData.userGroups)) {
        orderUserGroups = this.parseFilters('userGroups', inputData.userGroups);
      }
      if (!isEmpty(inputData.users)) {
        orderUsers = this.parseFilters('users', inputData.users);
      }
    }

    // Set Filter Data
    this.orderFilters.value.deliveryStatuses = orderDeliveryStatuses;
    this.orderFilters.value.fulfillmentGroups = orderFulfillmentGroups;
    this.orderFilters.value.organizations = orderOrganizations;
    this.orderFilters.value.orderStatuses = orderOrderStatuses;
    this.orderFilters.value.orderShippingTypes = orderShippingTypes;
    this.orderFilters.value.productionStatuses = orderProductionStatuses;
    this.orderFilters.value.seatTypes = orderSeatTypes;
    this.orderFilters.value.userGroups = orderUserGroups;
    this.orderFilters.value.users = orderUsers;
  }

  /**
   * Set Notifications Data from Response
   */
  setNotificationsData(inputData: any): void {
    if (!isEmpty(inputData.orderShippingNotifications)) {
      this.orderShippingNotifications = inputData.orderShippingNotifications;
    }
  }

  /**
   * Set Options Data from Response
   *
   * @since 4.71.0
   * @memberof WdOrdersV2Service
   */
  setOptionsData(inputData?: any): void {
    let orderFulfillmentGroups: Array<IFulfillmentGroupModel> = [];
    let orderShippingLoadingZones: Array<ILoadingZoneModel> = [];
    let orderShippingTypes: Array<IShippingTypeModel> = [];
    let orderShippingVehicleAssignments: Array<IVehicleAssignmentModel> = [];

    // Fulfillment groups
    if (!isEmpty(inputData.orderFulfillmentGroups)) {
      orderFulfillmentGroups = inputData.orderFulfillmentGroups;
    }
    // Shipping types
    if (!isEmpty(inputData)) {
      if (!isEmpty(inputData.orderShippingTypes)) {
        orderShippingTypes = inputData.orderShippingTypes.filter(
          (type: any) => {
            return type.status === 'active';
          },
        );
      }
      // Shipping vehicle assignments
      if (!isEmpty(inputData.orderShippingVehicleAssignments)) {
        orderShippingVehicleAssignments =
          inputData.orderShippingVehicleAssignments;
      }
      // Loading zones
      if (!isEmpty(inputData.orderShippingLoadingZones)) {
        orderShippingLoadingZones = inputData.orderShippingLoadingZones;
      }
    }

    // Set Options Data
    this.orderOptions.value.orderFulfillmentGroups = orderFulfillmentGroups;
    this.orderOptions.value.orderShippingTypes = orderShippingTypes;
    this.orderOptions.value.orderShippingVehicleAssignments = orderShippingVehicleAssignments;
    this.orderOptions.value.orderShippingLoadingZones = orderShippingLoadingZones;
  }

  /**
   * Set order labels for job and users
   *
   * @since 4.71.0
   * @memberof WdOrdersV2Service
   */
  setOrderLabels(order: IOrderModel) {
    if (order.job) {
      order.job.label = this.wdLocalSharedFunctions.buildJobLabel(order.job);
    }
    if (order.orderedBy) {
      order.orderedBy.fullName = this.wdLocalSharedFunctions.buildUserFullName(
        order.orderedBy,
      );
    }
    if (order.orderedFor) {
      order.orderedFor.fullName = this.wdLocalSharedFunctions.buildUserFullName(
        order.orderedFor,
      );
    }
  }

  /**
   * Set Visible Order Columns
   * @since 4.71.0
   * @memberof WdOrdersV2Service
   */
  setVisibleOrderColumns(orderType: string, selectedTab: string) {
    // Init columns
    const results: Array<any> = [];
    const columnOptions: Array<ITableColumnOptions> = this
      .orderListColumnsOptions;

    // Add columns based on restrictions
    columnOptions.forEach((column) => {
      // Column is toggled ON in settings
      const isColumnToggledOn = column.selected || !column.canToggle;
      switch (column.key) {
        case 'select':
          // Select column cannot be toggled off manually. Only based on permissions.
          if (this.wdEnvironmentService.permissions.canChangeOrderStatus) {
            results.push(column.key);
          }
          break;
        case 'lookahead':
          if (
            isColumnToggledOn &&
            orderType === this.wdLocalSharedFunctions.orderTypeKey.LOOKAHEAD
          ) {
            results.push(column.key);
          }
          break;
        case 'tag':
          if (
            isColumnToggledOn &&
            orderType !== this.wdLocalSharedFunctions.orderTypeKey.LOOKAHEAD
          ) {
            results.push(column.key);
          }
          break;
        default:
          if (isColumnToggledOn) {
            // Add standard columns
            results.push(column.key);
          }
      }
    });

    // Set columns
    this.orderListColumns = results;
    this.orderViewSettings.selected.columns[orderType][selectedTab] = results;
    this.saveLocalStorageSettings();
  }

  /**
   * Toggle Order Column Selections
   * @since 4.77.0
   * @memberof WdOrdersV2Service
   */
  toggleOrderColumnSelections(
    orderType: string,
    options: Array<any>,
    savedColumns: Array<string> = null,
    selectedTab: string = null,
  ) {
    const results = [];
    const columnOptions = angular.copy(options);

    columnOptions.forEach((column) => {
      column.selected = false;
      if (Array.isArray(savedColumns)) {
        // Get a list of columns which can't be toggled (core columns)
        const coreColumns = columnOptions
          .filter((column: ITableColumnOptions) => !column.canToggle)
          .map((column: ITableColumnOptions) => column.key);
        // Get a count of saved columns (not including core columns)
        const savedColumnsCount = savedColumns?.filter(
          (key: string) => !coreColumns.includes(key),
        )?.length;
        // Use saved columns to check if the column is selected; otherwise default to true
        column.selected =
          savedColumnsCount > 0 ? savedColumns.includes(column.key) : true;
      }

      // Check column visibility based on order type and selected tab
      const columnVisibleToLookaheads =
        orderType === this.wdLocalSharedFunctions.orderTypeKey.LOOKAHEAD &&
        column.visibility.orderTypes.includes('lookahead');
      const columnVisibleToWorkOrders =
        orderType === this.wdLocalSharedFunctions.orderTypeKey.WORK &&
        column.visibility.orderTypes.includes('work');
      const columnVisibleToTab = selectedTab
        ? column.visibility.selectedTabs?.includes(selectedTab)
        : true;
      const columnVisibleToAllTabs = column.visibility.selectedTabs?.includes(
        'all',
      );

      // Determine if the column can be toggled
      if (!columnVisibleToTab && !columnVisibleToAllTabs) {
        column.canToggle = false;
      }

      // Add the column to results if visible based on order type and selected tab
      if (
        (columnVisibleToLookaheads || columnVisibleToWorkOrders) &&
        (columnVisibleToTab || columnVisibleToAllTabs)
      ) {
        results.push(column);
      }
    });

    return results;
  }
}
